import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled, { css } from 'styled-components';

import { VisuallyHidden } from '@xing-com/crate-upsell-layouts';
import type { ContractPrice } from '@xing-com/crate-upsell-utils';
import { mediaSmallOrTiny } from '@xing-com/layout-tokens';
import { spaceXS } from '@xing-com/tokens';
import { BodyCopy, Headline } from '@xing-com/typography';

interface PricingBoxProps {
  price: ContractPrice;
  className?: string;
  isAlwaysWrapped?: boolean;
}

export function PricingBox({
  price,
  className = '',
  isAlwaysWrapped = false,
}: PricingBoxProps): JSX.Element {
  const { initial, renewal, discount, currency, current } = price;

  return (
    <PricingBoxWrapper
      data-testid="pricing-box"
      className={className}
      isAlwaysWrapped={isAlwaysWrapped}
    >
      <BodyCopy size="medium" noMargin>
        <Headline as="span" size="xxlarge" sizeConfined="xxxlarge">
          <FormattedNumber
            value={initial.monthly}
            style="currency"
            currency={currency}
          />
        </Headline>
        <FormattedMessage
          id="UPSELL_PURCHASE_OFFER_BOX_RUNTIME_DURATION_MONTH"
          defaultMessage=" /month"
        />
      </BodyCopy>
      <VisuallyHidden hasException={discount !== 0}>
        <BodyCopy
          size="xsmall"
          sizeConfined="medium"
          lineHeight="dense"
          noMargin
        >
          <FormattedMessage
            id="UPSELL_PURCHASE_OFFER_BOX_RUNTIME_RENEWAL_PRICE"
            defaultMessage=" (instead of {renewalPrice})"
            values={{
              renewalPrice: (
                <s>
                  <FormattedNumber
                    value={
                      current === null || current === 0
                        ? renewal.monthly
                        : current
                    }
                    style="currency"
                    currency={currency}
                  />
                </s>
              ),
            }}
          />
        </BodyCopy>
      </VisuallyHidden>
    </PricingBoxWrapper>
  );
}

const PricingBoxWrapper = styled.div<{ isAlwaysWrapped: boolean }>`
  ${({ isAlwaysWrapped }) =>
    isAlwaysWrapped
      ? null
      : css`
          @media ${mediaSmallOrTiny} {
            display: flex;
            align-items: baseline;
            flex-wrap: wrap;
            gap: ${spaceXS};
          }
        `}
`;
